import React from "react";

import {
  CalendarOutlined,
  CheckCircleFilled,
  InfoCircleOutlined,
  LockFilled,
  ReloadOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Button } from "antd";

import StatusLabelTag from "../../components/StatusLabelTag";
import SummaryCard from "./SummaryCard";

import { canMakePayment } from "../../../../../utils/Permissions";
import { canViewInvoice } from "../../utils";

import flaskWatermark from "../../../../../assets/images/subscriptions/flask.svg";

import "./SubscriptionSummaryCards.css";

const DEFAULT_DISABLED = {
  billingTerm: false,
  testsUsed: false,
  payable: false,
  topUp: false,
};

function resolveDisabledState(disabled, type, topUpMode) {
  if (topUpMode) {
    return {
      billingTerm: false,
      testsUsed: true,
      payable: true,
      topUp: false,
    };
  }

  if (disabled && typeof disabled === "object") {
    return { ...DEFAULT_DISABLED, ...disabled };
  }

  const allDisabled = disabled === true
    || type === "disabled"
    || type === "exhausted";

  return {
    billingTerm: allDisabled,
    testsUsed: allDisabled,
    payable: allDisabled,
    topUp: true,
  };
}

export default function SubscriptionSummaryCards({
  disabled,
  type,
  showTopUp,
  summaryCards = {},
  onViewBillingDetails,
  onTopUp,
}) {
  const {
    topUpMode,
    termLabel,
    termLength,
    termDateRange,
    billingCycle,
    testsUsed,
    testsTotal,
    totalCredits,
    payableAmount,
    dueDate,
    cycleResetDays,
    topUpAvailable,
    topUpLastPurchase,
    monthlyBillingAmount,
    subscriptionMode,
    subscriptionCreditsExhausted,
  } = summaryCards;
  const isRampup = subscriptionMode === "rampup";
  const rampupBillingAmount = isRampup && monthlyBillingAmount != null
    ? `$${Number(monthlyBillingAmount).toLocaleString()}`
    : null;
  const allowPayment = canMakePayment();
  const allowViewInvoice = canViewInvoice();
  const disabledState = resolveDisabledState(disabled, type, topUpMode);
  const {
    billingTerm: billingTermDisabled,
    testsUsed: testsUsedDisabled,
    payable: payableDisabled,
  } = disabledState;
  const topUpCreditsRemaining = Number(topUpAvailable) > 0;
  const topUpCardDisabled = !(
    topUpMode
    || (subscriptionCreditsExhausted && topUpCreditsRemaining)
  );

  const displayUsed = testsUsedDisabled && testsTotal != null
    ? testsTotal
    : (testsUsed ?? null);
  const displayTotal = totalCredits ?? testsTotal;
  const usedPercent = displayTotal
    ? Math.round((Number(displayUsed || 0) / displayTotal) * 100)
    : null;

  const rootClass = [
    "SubscriptionSummaryCards",
    showTopUp ? "" : "SubscriptionSummaryCards--withoutTopUp",
  ].filter(Boolean).join(" ");

  return (
    <section className={rootClass}>
      <SummaryCard
        disabled={billingTermDisabled}
        title="Billing Term"
        badge={isRampup ? "Currently in Rampup" : (termLabel || null)}
        badgeTone="cool"
        value={rampupBillingAmount ?? (termLength || null)}
        valueTone={rampupBillingAmount != null ? "default" : "secondary"}
        valueLabel={rampupBillingAmount != null ? "Billing amount" : null}
        detail={termDateRange || null}
        footer={billingCycle ? (
          <>
            {"Billing cycle: "}
            <strong>{billingCycle}</strong>
          </>
        ) : null}
        footerTone="muted"
      />

      <SummaryCard
        disabled={testsUsedDisabled}
        sections={[
          {
            key: "tests-used",
            disabled: testsUsedDisabled,
            title: "Total Credits Used",
            badge: testsUsedDisabled ? "Exhausted" : "Active",
            badgeTone: testsUsedDisabled ? "neutral" : "positive",
            value: displayUsed != null ? displayUsed.toLocaleString() : null,
            valueTone: testsUsedDisabled ? "muted" : "positive",
            detail: displayTotal != null && usedPercent != null
              ? `of ${displayTotal.toLocaleString()} (${usedPercent}%)`
              : null,
            detailMuted: true,
            detailInline: true,
            footer: testsUsedDisabled ? (
              <StatusLabelTag tone="info" icon={<InfoCircleOutlined />} wrap>
                Your subscription credits are exhausted, you are using your top-up pool.
              </StatusLabelTag>
            ) : (
              <StatusLabelTag
                tone="healthy"
                variant="plain"
                icon={<CheckCircleFilled />}
              >
                All categories healthy
              </StatusLabelTag>
            ),
            footerTone: "default",
          },
          {
            key: "payable",
            disabled: payableDisabled,
            title: "Payable this cycle",
            headerExtra: allowViewInvoice ? (
              <Button
                type="text"
                onClick={onViewBillingDetails}
              >
                View billing details
                <RightOutlined />
              </Button>
            ) : null,
            value: payableAmount != null
              ? `$${Number(payableAmount).toLocaleString()}`
              : null,
            detail: dueDate ? `Due ${dueDate}` : null,
            detailMuted: true,
            detailInline: true,
            watermark: flaskWatermark,
            footer: cycleResetDays != null ? (
              <StatusLabelTag tone="muted" icon={<ReloadOutlined />}>
                {`Cycle resets in ${cycleResetDays} days`}
              </StatusLabelTag>
            ) : null,
          },
        ]}
      />

      {showTopUp && (
        <SummaryCard
          className="SubscriptionSummaryCards__TopUp"
          disabled={topUpCardDisabled}
          title="Top-up Pool"
          badge={topUpCardDisabled ? "Inactive" : "Active"}
          badgeTone={topUpCardDisabled ? "neutral" : "cool"}
          headerExtra={!topUpCardDisabled && allowPayment ? (
            <Button
              type="text"
              onClick={onTopUp}
            >
              Top-up Credits
              <RightOutlined />
            </Button>
          ) : null}
          value={topUpAvailable != null ? Number(topUpAvailable).toLocaleString() : 0}
          valueTone={topUpCardDisabled ? "muted" : "positive"}
          detail="credits available"
          detailMuted
          detailInline
          meta={topUpLastPurchase ? (
            <>
              <CalendarOutlined />
              {`Last purchase on ${topUpLastPurchase}`}
            </>
          ) : null}
          decoration={topUpCardDisabled ? <LockFilled /> : null}
          footer={(
            <StatusLabelTag tone="muted" icon={<InfoCircleOutlined />} wrap>
              {topUpCardDisabled
                ? "You'll start using top-up credits after your subscription credits are exhausted."
                : "Need more credits? Once a category runs out, top-up to continue testing."}
            </StatusLabelTag>
          )}
        />
      )}
    </section>
  );
}
